import { useMemo, useState } from 'react';
import { AsyncButton } from '@/components/common/AsyncButton';
import { Select2 } from '@/components/forms/Select2';
import { Select2Multi } from '@/components/forms/Select2Multi';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import type { ProviderDetailsDto } from '@/api/types/providerFunds';
import {
  BALANCE_TYPES,
  MANUAL_DEPOSIT_VALUE,
  MANUAL_WITHDRAWAL_VALUE,
} from '@/api/types/providerFunds';
import type { ProviderFundsConfig } from './config';
import { yesterdayIso } from './config';

interface ManualTransactionCardProps {
  config: ProviderFundsConfig;
  providerId: string;
  providerName: string;
  details: ProviderDetailsDto | undefined;
  canViewRealCashBalance: boolean;
  onCreated: () => void;
  onCancel: () => void;
}

const OPERATION_OPTIONS = [
  { value: MANUAL_DEPOSIT_VALUE, label: 'Deposit' },
  { value: MANUAL_WITHDRAWAL_VALUE, label: 'Withdrawal' },
];

export function ManualTransactionCard({
  config,
  providerId,
  providerName,
  details,
  canViewRealCashBalance,
  onCreated,
  onCancel,
}: ManualTransactionCardProps) {
  const toast = useToast();
  const yesterday = yesterdayIso();

  const openingDate = details?.startingBalanceDate
    ? details.startingBalanceDate.slice(0, 10)
    : null;
  const lockedThroughDay = config.lockAwareManualCard && details?.lockedThrough ? details.lockedThrough.slice(0, 10) : null;

  const [to, setTo] = useState<string>(yesterday);
  const [type, setType] = useState<number>(MANUAL_DEPOSIT_VALUE);
  const [balanceTypes, setBalanceTypes] = useState<string[]>([]);
  const [amount, setAmount] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const balanceTypeOptions = useMemo(
    () =>
      BALANCE_TYPES.filter((b) => !(b.requiresRealCash && !canViewRealCashBalance)).map((b) => ({
        value: b.value,
        label: b.text,
      })),
    [canViewRealCashBalance],
  );

  // A chosen date <= lockedThroughDay is locked (PA-only).
  const isLocked = Boolean(lockedThroughDay && to && to <= lockedThroughDay);

  function handleDate(v: string) {
    if (openingDate && v < openingDate) {
      setTo(openingDate);
      toast.info('Date adjusted to the opening balance date.');
    } else {
      setTo(v);
    }
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (balanceTypes.length === 0) e.balanceTypes = 'Select at least one balance type.';
    const amt = parseFloat(amount.replace(/,/g, ''));
    if (!amount || Number.isNaN(amt) || amt <= 0) e.amount = 'Enter an amount greater than 0.';
    if (!comment.trim()) e.comment = 'Comment is required.';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function create() {
    if (isLocked) {
      toast.error('This period is locked. Transactions cannot be added to a locked period.');
      return;
    }
    if (!validate()) return;
    try {
      const res = await config.api.addManualTransaction({
        providerId,
        type,
        balanceTypes: balanceTypes.map(Number),
        amount: parseFloat(amount.replace(/,/g, '')),
        comment: comment.trim(),
        to,
      });
      if (res.isSuccess) {
        toast.success(res.message);
        onCreated();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to create the transaction.');
    }
  }

  return (
    <div className="card custom-card mb-3">
      <div className="card-header">
        <h6 className="card-title mb-0">Create Manual Transaction For {providerName}</h6>
      </div>
      <div className="card-body">
        <div className="row g-3">
          <div className="col-md-4 col-lg-3">
            <DateRangePicker label="To" mode="single" value={to} maxDate={yesterday} minDate={openingDate} onChange={handleDate} />
          </div>
          <div className="col-md-4 col-lg-3">
            <Select2 label="Operation Type" value={type} options={OPERATION_OPTIONS} searchable={false} allowClear={false} onChange={(v) => setType(Number(v))} />
          </div>
          <div className="col-md-4 col-lg-3">
            <Select2Multi label="Balance Type" value={balanceTypes} options={balanceTypeOptions} searchable={false} error={errors.balanceTypes} onChange={setBalanceTypes} />
          </div>
          <div className="col-md-4 col-lg-3">
            <label className="form-label">Currency</label>
            <input className="form-control" readOnly value={details?.currency ?? ''} />
          </div>
          <div className="col-md-4 col-lg-3">
            <label className="form-label">Amount</label>
            <input
              type="number"
              min={0}
              step="any"
              className={`form-control${errors.amount ? ' is-invalid' : ''}`}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            {errors.amount && <div className="invalid-feedback d-block">{errors.amount}</div>}
          </div>
          <div className="col-md-8 col-lg-6">
            <label className="form-label">Comment</label>
            <input
              className={`form-control${errors.comment ? ' is-invalid' : ''}`}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            {errors.comment && <div className="invalid-feedback d-block">{errors.comment}</div>}
          </div>
        </div>

        {isLocked && (
          <div className="text-danger small mt-2">This period is locked. Choose a later date.</div>
        )}

        <div className="d-flex justify-content-end gap-2 mt-3">
          <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-plus" onClick={create} disabled={isLocked}>
            Create
          </AsyncButton>
          <button type="button" className="btn btn-light btn-sm" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
